import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Switch,
  Table,
  Typography,
} from '@mui/joy';
import { FaChevronLeft, FaChevronRight, FaPen, FaTrash } from 'react-icons/fa';
import { IconWrapper } from '../../../components/IconWrapper';
import { useSupabaseAuth } from '../../../contexts/SupabaseAuthContext';
import { useProfile } from '../../../hooks/useProfile';
import { formatGbp } from '../../accounts/arrangements.model';
import {
  deductionTotals,
  formatMonth,
  monthKey,
  netFromGross,
  RECURRENCE_INTERVALS,
  recurrenceFallsIn,
  recurrenceLabel,
  recurrenceOccurrences,
  shiftMonth,
  signoffOverdue,
  signoffState,
  toIsoDate,
  validateDeduction,
  validateRecurrence,
  vatFromGross,
  type LedgerPerson,
  type WageDeduction,
  type WageDeductionInput,
  type WageDeductionRecurrence,
  type WageDeductionRecurrenceInput,
  type WageDeductionSignoff,
} from '../ledger.model';
import { ledgerService } from '../ledger.service';

/** Months before this predate the ledger, so they are never flagged as missing a sign-off. */
const LEDGER_START_MONTH = '2026-10-01';
const OVERDUE_LOOKBACK_MONTHS = 6;

const amountCell = { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const };
const iconStyle = { fontSize: 12, display: 'inline-flex' } as const;

/** What the deduction modal is editing. */
type ModalValue =
  | { kind: 'new'; profileId: string }
  | { kind: 'row'; row: WageDeduction }
  | { kind: 'series'; recurrence: WageDeductionRecurrence };

function affirmedAt(value: string | null): string {
  return value ? new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '';
}

/**
 * Personal items bought through the business. The person repays the net amount from that
 * month's salary; the business keeps the VAT it reclaims. Each month the person affirms
 * the deductions and an admin approves them, which locks the month.
 */
export const WageDeductionsTab: React.FC<{ people: LedgerPerson[] }> = ({ people }) => {
  const { user } = useSupabaseAuth();
  const { isAdmin } = useProfile();
  const today = toIsoDate(new Date());
  const [month, setMonth] = useState(() => monthKey(today));
  const [rows, setRows] = useState<WageDeduction[]>([]);
  const [signoffs, setSignoffs] = useState<WageDeductionSignoff[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [recurrences, setRecurrences] = useState<WageDeductionRecurrence[]>([]);
  const [editing, setEditing] = useState<ModalValue | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const lookbackStart = shiftMonth(monthKey(today), -OVERDUE_LOOKBACK_MONTHS);
      const from = month < lookbackStart ? month : lookbackStart;
      const [deductionRows, signoffRows, recurrenceRows] = await Promise.all([
        ledgerService.listDeductions(month),
        ledgerService.listSignoffs(from, monthKey(today) > month ? monthKey(today) : month),
        ledgerService.listRecurrences(),
      ]);
      setRows(deductionRows);
      setSignoffs(signoffRows);
      setRecurrences(recurrenceRows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load wage deductions');
    } finally {
      setLoading(false);
    }
  }, [month, today]);

  useEffect(() => {
    void load();
  }, [load]);

  const signoffFor = (profileId: string, forMonth: string) =>
    signoffs.find((row) => row.profileId === profileId && row.deductionMonth === forMonth) ?? null;

  // Ended months, from the ledger's start, that someone has not fully signed off.
  const overdue = useMemo(() => {
    const items: { person: LedgerPerson; month: string }[] = [];
    for (let i = OVERDUE_LOOKBACK_MONTHS; i >= 1; i -= 1) {
      const past = shiftMonth(monthKey(today), -i);
      if (past < LEDGER_START_MONTH) continue;
      for (const person of people) {
        if (signoffOverdue(past, signoffFor(person.profileId, past), today)) items.push({ person, month: past });
      }
    }
    return items;
  }, [people, signoffs, today]); // eslint-disable-line react-hooks/exhaustive-deps

  const affirm = async (profileId: string) => {
    setBusy(profileId);
    setError(null);
    try {
      const row = await ledgerService.affirmMonth(profileId, month);
      setSignoffs((current) => [...current.filter((s) => !(s.profileId === profileId && s.deductionMonth === row.deductionMonth)), row]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to affirm');
    } finally {
      setBusy(null);
    }
  };

  const withdraw = async (profileId: string) => {
    if (!window.confirm('Withdraw the affirmation for this month? Both the person and an admin will need to affirm it again.')) return;
    setBusy(profileId);
    setError(null);
    try {
      await ledgerService.withdrawMonth(profileId, month);
      setSignoffs((current) => current.filter((s) => !(s.profileId === profileId && s.deductionMonth === month)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to withdraw');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (row: WageDeduction) => {
    if (!window.confirm(`Delete "${row.description}"?`)) return;
    try {
      await ledgerService.deleteDeduction(row.id);
      setRows((current) => current.filter((item) => item.id !== row.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton size="sm" variant="outlined" title="Previous month" onClick={() => setMonth((m) => shiftMonth(m, -1))}>
          <IconWrapper icon={FaChevronLeft} style={iconStyle} />
        </IconButton>
        <Typography level="title-lg" sx={{ minWidth: 170, textAlign: 'center' }}>{formatMonth(month)}</Typography>
        <IconButton size="sm" variant="outlined" title="Next month" onClick={() => setMonth((m) => shiftMonth(m, 1))}>
          <IconWrapper icon={FaChevronRight} style={iconStyle} />
        </IconButton>
        {month !== monthKey(today) && (
          <Button size="sm" variant="plain" onClick={() => setMonth(monthKey(today))}>This month</Button>
        )}
      </Stack>

      {overdue.length > 0 && (
        <Alert color="warning" variant="soft">
          <Stack spacing={0.5}>
            <Typography level="title-sm">Month-end sign-off outstanding</Typography>
            {overdue.map(({ person, month: past }) => {
              const state = signoffState(signoffFor(person.profileId, past));
              return (
                <Typography key={`${person.profileId}-${past}`} level="body-sm">
                  <Button size="sm" variant="plain" sx={{ p: 0, minHeight: 0 }} onClick={() => setMonth(past)}>{formatMonth(past)}</Button>
                  {' — '}{person.displayName}: {state === 'open' ? 'not affirmed' : 'awaiting admin approval'}
                </Typography>
              );
            })}
          </Stack>
        </Alert>
      )}

      {error && <Alert color="danger">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : people.length === 0 ? (
        <Alert color="neutral">No one is set up for wage deductions.</Alert>
      ) : (
        people.map((person) => {
          const personRows = rows.filter((row) => row.profileId === person.profileId);
          const totals = deductionTotals(personRows);
          const signoff = signoffFor(person.profileId, month);
          const state = signoffState(signoff);
          const locked = state !== 'open';
          const isPerson = user?.id === person.profileId;
          const personRecurrences = recurrences.filter((recurrence) =>
            recurrence.profileId === person.profileId && (!recurrence.endMonth || recurrence.endMonth >= month));
          const changedSinceAffirmed = signoff != null && (signoff.netTotal !== totals.net || signoff.entryCount !== totals.count);

          return (
            <Sheet key={person.profileId} variant="outlined" sx={{ borderRadius: 'sm', p: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography level="title-md">{person.displayName}</Typography>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={state === 'affirmed' ? 'success' : state === 'awaiting-admin' ? 'primary' : signoffOverdue(month, signoff, today) ? 'warning' : 'neutral'}
                    >
                      {state === 'affirmed' ? 'Affirmed' : state === 'awaiting-admin' ? 'Awaiting admin' : 'Open'}
                    </Chip>
                  </Stack>
                  <Button size="sm" variant="outlined" disabled={locked} onClick={() => setEditing({ kind: 'new', profileId: person.profileId })}>
                    Add item
                  </Button>
                </Stack>

                <Box sx={{ overflow: 'auto' }}>
                  <Table size="sm" sx={{ minWidth: 720 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 100 }}>Bought</th>
                        <th>Item</th>
                        <th style={{ width: 160 }}>Supplier</th>
                        <th style={{ ...amountCell, width: 100 }}>Gross</th>
                        <th style={{ ...amountCell, width: 90 }}>VAT</th>
                        <th style={{ ...amountCell, width: 110 }}>Deduct (net)</th>
                        <th style={{ width: 72 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {personRows.map((row) => (
                        <tr key={row.id}>
                          <td>{row.purchasedOn}</td>
                          <td>
                            {row.description}
                            {row.recurrenceId && <Chip size="sm" variant="outlined" sx={{ ml: 1 }}>Recurring</Chip>}
                          </td>
                          <td>{row.supplier || '—'}</td>
                          <td style={amountCell}>{formatGbp(row.grossAmount)}</td>
                          <td style={amountCell}>{row.vatAmount ? formatGbp(row.vatAmount) : '—'}</td>
                          <td style={{ ...amountCell, fontWeight: 600 }}>{formatGbp(row.netAmount)}</td>
                          <td>
                            {!locked && (
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                <IconButton size="sm" variant="plain" title="Edit" onClick={() => setEditing({ kind: 'row', row })}>
                                  <IconWrapper icon={FaPen} style={iconStyle} />
                                </IconButton>
                                <IconButton size="sm" variant="plain" color="danger" title="Delete" onClick={() => void remove(row)}>
                                  <IconWrapper icon={FaTrash} style={iconStyle} />
                                </IconButton>
                              </Stack>
                            )}
                          </td>
                        </tr>
                      ))}
                      {personRows.length === 0 && (
                        <tr>
                          <td colSpan={7}>
                            <Typography level="body-sm" textColor="neutral.500" sx={{ py: 1, textAlign: 'center' }}>
                              Nothing to deduct this month.
                            </Typography>
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {personRows.length > 0 && (
                      <tfoot>
                        <tr>
                          <th colSpan={3}>Total</th>
                          <th style={amountCell}>{formatGbp(totals.gross)}</th>
                          <th style={amountCell}>{formatGbp(totals.vat)}</th>
                          <th style={amountCell}>{formatGbp(totals.net)}</th>
                          <th />
                        </tr>
                      </tfoot>
                    )}
                  </Table>
                </Box>

                {personRecurrences.length > 0 && (
                  <Stack spacing={0.5}>
                    <Typography level="body-xs" textColor="neutral.500">Recurring items</Typography>
                    {personRecurrences.map((recurrence) => (
                      <Stack key={recurrence.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography level="body-sm">
                          {recurrence.description} · {formatGbp(netFromGross(recurrence.grossAmount, recurrence.vatAmount))} net
                        </Typography>
                        <Typography level="body-xs" textColor="neutral.500">
                          {recurrenceLabel(recurrence)}{recurrenceFallsIn(recurrence, month) ? '' : ' (not this month)'}
                        </Typography>
                        <Button size="sm" variant="plain" sx={{ minHeight: 0, py: 0 }} onClick={() => setEditing({ kind: 'series', recurrence })}>
                          Edit
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                )}

                <Sheet variant="soft" color={state === 'affirmed' ? 'success' : 'neutral'} sx={{ borderRadius: 'sm', p: 1.5 }}>
                  <Stack spacing={1}>
                    <Checkbox
                      checked={signoff?.personAffirmedAt != null}
                      disabled={busy === person.profileId || (signoff?.personAffirmedAt != null ? !(isPerson || isAdmin) : !isPerson)}
                      onChange={(event) => (event.target.checked ? void affirm(person.profileId) : void withdraw(person.profileId))}
                      label={`${person.displayName}: I confirm ${formatGbp(totals.net)} is to be deducted from my ${formatMonth(month)} salary.`}
                    />
                    {signoff?.personAffirmedAt && (
                      <Typography level="body-xs" textColor="neutral.600" sx={{ pl: 3.5 }}>
                        Affirmed {affirmedAt(signoff.personAffirmedAt)} for {formatGbp(signoff.netTotal)}
                      </Typography>
                    )}
                    {!isPerson && state === 'open' && (
                      <Typography level="body-xs" textColor="neutral.500" sx={{ pl: 3.5 }}>
                        Only {person.displayName} can affirm this.
                      </Typography>
                    )}
                    <Checkbox
                      checked={signoff?.adminAffirmedAt != null}
                      disabled={busy === person.profileId || !isAdmin || state === 'open'}
                      onChange={(event) => (event.target.checked ? void affirm(person.profileId) : void withdraw(person.profileId))}
                      label="Admin: approved for payroll."
                    />
                    {signoff?.adminAffirmedAt && (
                      <Typography level="body-xs" textColor="neutral.600" sx={{ pl: 3.5 }}>
                        Approved {affirmedAt(signoff.adminAffirmedAt)}
                      </Typography>
                    )}
                    {changedSinceAffirmed && (
                      <Alert size="sm" color="warning">
                        The deductions no longer match what was affirmed. Withdraw and affirm again.
                      </Alert>
                    )}
                  </Stack>
                </Sheet>
              </Stack>
            </Sheet>
          );
        })
      )}

      <Typography level="body-xs" textColor="neutral.500">
        Enter the gross amount paid at the till. If the business reclaims the VAT, it is taken off and only the net amount is deducted from salary. Once a person affirms a month, its items are locked. Recurring items add themselves to each month they fall in; affirmed months are never changed.
      </Typography>

      <DeductionModal
        value={editing}
        people={people}
        month={month}
        recurrences={recurrences}
        onEditSeries={(recurrence) => setEditing({ kind: 'series', recurrence })}
        onClose={() => setEditing(null)}
        onSaved={(row) => {
          setEditing(null);
          if (!row) {
            void load();
            return;
          }
          setRows((current) => {
            const others = current.filter((item) => item.id !== row.id);
            return row.deductionMonth === month ? [...others, row] : others;
          });
        }}
      />
    </Stack>
  );
};

const DeductionModal: React.FC<{
  value: ModalValue | null;
  people: LedgerPerson[];
  month: string;
  recurrences: WageDeductionRecurrence[];
  onEditSeries: (recurrence: WageDeductionRecurrence) => void;
  onClose: () => void;
  /** Called with the saved row, or with nothing when the month needs reloading. */
  onSaved: (row?: WageDeduction) => void;
}> = ({ value, people, month, recurrences, onEditSeries, onClose, onSaved }) => {
  const row = value?.kind === 'row' ? value.row : null;
  const series = value?.kind === 'series' ? value.recurrence : null;
  const rowSeries = row?.recurrenceId ? recurrences.find((recurrence) => recurrence.id === row.recurrenceId) ?? null : null;
  const [profileId, setProfileId] = useState<string | null>(null);
  const [purchasedOn, setPurchasedOn] = useState('');
  const [deductionMonth, setDeductionMonth] = useState('');
  const [description, setDescription] = useState('');
  const [supplier, setSupplier] = useState('');
  const [grossText, setGrossText] = useState('');
  const [reclaimVat, setReclaimVat] = useState(true);
  const [autoVat, setAutoVat] = useState(true);
  const [vatText, setVatText] = useState('');
  const [repeats, setRepeats] = useState(false);
  const [intervalMonths, setIntervalMonths] = useState(1);
  const [endMonth, setEndMonth] = useState('');
  const [applyFrom, setApplyFrom] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!value) return;
    const today = toIsoDate(new Date());
    const source = row ?? series;
    setProfileId(value.kind === 'new' ? value.profileId : (source as { profileId: string }).profileId);
    setPurchasedOn(row?.purchasedOn ?? (monthKey(today) === month ? today : month));
    setDeductionMonth((row?.deductionMonth ?? series?.startMonth ?? month).slice(0, 7));
    setDescription(source?.description ?? '');
    setSupplier(source?.supplier ?? '');
    setGrossText(source ? String(source.grossAmount) : '');
    setReclaimVat(source ? source.vatAmount > 0 : true);
    setAutoVat(source ? source.vatAmount === vatFromGross(source.grossAmount) : true);
    setVatText(source ? String(source.vatAmount) : '');
    setRepeats(series != null);
    setIntervalMonths(series?.intervalMonths ?? 1);
    setEndMonth(series?.endMonth?.slice(0, 7) ?? '');
    setApplyFrom((series && series.startMonth > month ? series.startMonth : month).slice(0, 7));
    setError(null);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const gross = Number(grossText);
  const vat = !reclaimVat ? 0 : autoVat ? vatFromGross(gross) : Number(vatText || 0);
  const net = Number.isFinite(gross) && Number.isFinite(vat) ? netFromGross(gross, vat) : 0;
  const schedule = { intervalMonths, startMonth: `${deductionMonth}-01`, endMonth: endMonth ? `${endMonth}-01` : null };
  const occurrences = deductionMonth ? recurrenceOccurrences(schedule) : null;

  const run = async (action: () => Promise<WageDeduction | void>) => {
    setSaving(true);
    setError(null);
    try {
      const saved = await action();
      onSaved(saved || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    if (repeats) {
      const input: WageDeductionRecurrenceInput = {
        profileId: profileId || '',
        description,
        supplier: supplier || null,
        grossAmount: gross,
        vatAmount: vat,
        ...schedule,
      };
      const problem = validateRecurrence(input);
      if (problem) {
        setError(problem);
        return;
      }
      void run(async () => {
        if (series) await ledgerService.updateRecurrence(series.id, input, `${applyFrom}-01`);
        else await ledgerService.createRecurrence(input);
      });
      return;
    }
    const input: WageDeductionInput = {
      profileId: profileId || '',
      deductionMonth: `${deductionMonth}-01`,
      purchasedOn,
      description,
      supplier: supplier || null,
      grossAmount: gross,
      vatAmount: vat,
    };
    const problem = validateDeduction(input);
    if (problem) {
      setError(problem);
      return;
    }
    void run(() => ledgerService.saveDeduction(input, row?.id));
  };

  const removeSeries = () => {
    if (!series) return;
    const from = formatMonth(`${applyFrom}-01`);
    if (!window.confirm(`Delete this recurring item? It is removed from ${from} onwards, except in affirmed months. Earlier months keep their deduction.`)) return;
    void run(() => ledgerService.deleteRecurrence(series.id, `${applyFrom}-01`));
  };

  const title = series ? 'Edit recurring item' : row ? 'Edit deduction' : 'Add deduction';

  return (
    <Modal open={value != null} onClose={() => !saving && onClose()}>
      <ModalDialog sx={{ width: { xs: '100%', sm: 520 }, maxHeight: '92vh', overflow: 'auto' }}>
        <Typography level="title-lg">{title}</Typography>
        <Stack spacing={1.5}>
          {row?.recurrenceId && (
            <Alert size="sm" color="primary" variant="soft">
              <Stack spacing={0.5}>
                <Typography level="body-sm">
                  Part of a recurring item{rowSeries ? ` (${recurrenceLabel(rowSeries)})` : ''}. Changes here apply to {formatMonth(row.deductionMonth)} only.
                </Typography>
                {rowSeries && (
                  <Button size="sm" variant="plain" sx={{ alignSelf: 'flex-start', p: 0, minHeight: 0 }} onClick={() => onEditSeries(rowSeries)}>
                    Edit the recurring item instead
                  </Button>
                )}
              </Stack>
            </Alert>
          )}
          <Stack direction="row" spacing={1}>
            <FormControl sx={{ flex: 1 }} required>
              <FormLabel>For</FormLabel>
              <Select value={profileId} onChange={(_, next) => setProfileId(next)}>
                {people.map((person) => <Option key={person.profileId} value={person.profileId}>{person.displayName}</Option>)}
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>{repeats ? 'Starts' : 'Deduct from'}</FormLabel>
              <Input type="month" value={deductionMonth} onChange={(event) => setDeductionMonth(event.target.value)} />
            </FormControl>
          </Stack>
          <FormControl required>
            <FormLabel>Item</FormLabel>
            <Input autoFocus value={description} placeholder="e.g. 10 x oak sleepers" onChange={(event) => setDescription(event.target.value)} />
          </FormControl>
          <Stack direction="row" spacing={1}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Supplier</FormLabel>
              <Input value={supplier} placeholder="e.g. Builders merchant" onChange={(event) => setSupplier(event.target.value)} />
            </FormControl>
            {!repeats && (
              <FormControl sx={{ width: 160 }}>
                <FormLabel>Bought on</FormLabel>
                <Input type="date" value={purchasedOn} onChange={(event) => setPurchasedOn(event.target.value)} />
              </FormControl>
            )}
          </Stack>
          <FormControl required>
            <FormLabel>Gross paid £ (inc VAT){repeats ? ' each time' : ''}</FormLabel>
            <Input type="number" inputMode="decimal" value={grossText} onChange={(event) => setGrossText(event.target.value)} />
          </FormControl>
          <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
            <Box>
              <FormLabel>Business reclaims the VAT</FormLabel>
              <FormHelperText>Off for items with no VAT or no VAT receipt.</FormHelperText>
            </Box>
            <Switch checked={reclaimVat} onChange={(event) => setReclaimVat(event.target.checked)} />
          </FormControl>
          {reclaimVat && (
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>VAT £</FormLabel>
                <Input
                  type="number"
                  value={autoVat ? (Number.isFinite(gross) && gross > 0 ? vatFromGross(gross).toFixed(2) : '') : vatText}
                  disabled={autoVat}
                  onChange={(event) => setVatText(event.target.value)}
                />
              </FormControl>
              <Checkbox
                sx={{ pb: 1 }}
                label="20%"
                checked={autoVat}
                onChange={(event) => {
                  setAutoVat(event.target.checked);
                  if (!event.target.checked) setVatText(vatFromGross(gross).toFixed(2));
                }}
              />
            </Stack>
          )}

          {!row && (
            <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
              <Box>
                <FormLabel>Repeats</FormLabel>
                <FormHelperText>Deduct the same amount at an interval, optionally until an end month.</FormHelperText>
              </Box>
              <Switch checked={repeats} disabled={series != null} onChange={(event) => setRepeats(event.target.checked)} />
            </FormControl>
          )}
          {repeats && (
            <>
              <Stack direction="row" spacing={1}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>How often</FormLabel>
                  <Select value={intervalMonths} onChange={(_, next) => next && setIntervalMonths(next)}>
                    {RECURRENCE_INTERVALS.map((option) => <Option key={option.months} value={option.months}>{option.label}</Option>)}
                  </Select>
                </FormControl>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Until (optional)</FormLabel>
                  <Input type="month" value={endMonth} onChange={(event) => setEndMonth(event.target.value)} />
                </FormControl>
              </Stack>
              {deductionMonth && (
                <Typography level="body-xs" textColor="neutral.600">
                  {recurrenceLabel(schedule)}
                  {occurrences != null && occurrences > 0 && ` · ${occurrences} deduction${occurrences === 1 ? '' : 's'}, ${formatGbp(net * occurrences)} net in total`}
                </Typography>
              )}
              {series && (
                <FormControl>
                  <FormLabel>Apply changes from</FormLabel>
                  <Input type="month" value={applyFrom} onChange={(event) => setApplyFrom(event.target.value)} />
                  <FormHelperText>Months before this, and affirmed months, keep what they have.</FormHelperText>
                </FormControl>
              )}
            </>
          )}

          <Sheet variant="soft" color="primary" sx={{ p: 1.5, borderRadius: 'sm' }}>
            <Typography level="title-sm">Deduct from salary: {formatGbp(net)}{repeats ? ' each time' : ''}</Typography>
          </Sheet>
          {error && <Alert color="danger">{error}</Alert>}
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Box>
              {series && <Button variant="plain" color="danger" onClick={removeSeries} disabled={saving}>Delete</Button>}
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="plain" color="neutral" onClick={onClose} disabled={saving}>Cancel</Button>
              <Button onClick={save} loading={saving}>Save</Button>
            </Stack>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};
